'use client';

import { useState } from 'react';
import ActionIconButton from '@/components/ActionIconButton';
import { CategoryIcon } from '@/components/CategoryIcon';
import { useAppSettings } from '@/hooks/useAppSettings';
import { formatMoney } from '@/lib/money';
import { Category, Tag, Transaction } from '@/lib/types';

export default function TransactionList({
  transactions,
  categories,
  tags,
  onEdit,
  onDelete,
  emptyLabel = "Aucune transaction pour l'instant. Ajoutez votre première opération quand vous êtes prêt.",
}: {
  transactions: Transaction[];
  categories: Category[];
  tags: Tag[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  emptyLabel?: string;
}) {
  const [openActionId, setOpenActionId] = useState<string | null>(null);
  const { settings } = useAppSettings();
  const money = (value: number) => formatMoney(value, settings.currency);

  return (
    <div className="space-y-3">
      {transactions.length === 0 ? (
        <div className="card-muted text-sm">{emptyLabel}</div>
      ) : (
        transactions.map((tx) => {
          const category = categories.find((item) => item.id === tx.categoryId);
          const isIncome = tx.type === 'income';
          const txTags = tx.tagIds
            .map((tagId) => tags.find((item) => item.id === tagId))
            .filter((tag): tag is Tag => Boolean(tag));

          return (
            <div key={tx.id} className="rounded-2xl border border-slate-800 bg-slate-900 p-3 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    {category ? <CategoryIcon category={category} className="h-7 w-7" /> : null}
                    <span className="min-w-0 font-semibold text-slate-100">{category?.name ?? (isIncome ? 'Entrée' : 'Dépense')}</span>
                    <span className="text-sm text-slate-500">·</span>
                    <span className="text-sm text-slate-400">{tx.date}</span>
                    {txTags.map((tag) => (
                      <span key={tag.id} className="rounded-full bg-slate-800 px-2 py-0.5 text-xs font-semibold text-slate-300">
                        {tag.name}
                      </span>
                    ))}
                  </div>
                  {tx.note ? <p className="mt-2 text-sm text-slate-300">{tx.note}</p> : null}
                </div>
                <div className="flex shrink-0 items-start justify-between gap-2 sm:justify-end">
                  <div className="text-left sm:text-right">
                    <p className={`text-base font-semibold ${isIncome ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {isIncome ? '+' : '-'}{money(tx.amount)}
                    </p>
                    <p className="text-xs font-medium text-slate-400">{isIncome ? 'Entrée' : 'Sortie'}</p>
                  </div>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setOpenActionId(openActionId === tx.id ? null : tx.id)}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 bg-slate-950 text-slate-300 shadow-none transition hover:border-brand-400 hover:text-brand-200"
                      title="Actions"
                      aria-label="Actions de la transaction"
                      aria-expanded={openActionId === tx.id}
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                        <circle cx="5" cy="12" r="2" />
                        <circle cx="12" cy="12" r="2" />
                        <circle cx="19" cy="12" r="2" />
                      </svg>
                    </button>
                    {openActionId === tx.id ? (
                      <div className="absolute right-0 top-12 z-10 flex gap-1.5 rounded-2xl border border-slate-700 bg-slate-950 p-1.5 shadow-xl">
                        <ActionIconButton
                          label="Modifier la transaction"
                          icon="edit"
                          onClick={() => {
                            setOpenActionId(null);
                            onEdit(tx.id);
                          }}
                        />
                        <ActionIconButton
                          label="Supprimer la transaction"
                          icon="trash"
                          tone="danger"
                          onClick={() => {
                            setOpenActionId(null);
                            onDelete(tx.id);
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
